import type {
  BUTTON_CHOICE_LAYOUT_TYPE,
  IconChoice,
  IMAGE_CHOICE_UI_SUBTYPE,
  ImageChoice
} from '../choices'
import type { BLOCK_TYPE } from '../enums'
import type { FileId } from '../ids'

import type { BlockBaseSettings, IBlock } from './base'

export enum SINGLE_CHOICE_UI_TYPE {
  BUTTON = 'button',
  RADIO = 'radio',
  DROPDOWN = 'dropdown',
  IMAGE = 'image'
}

type SharedSettings = {
  label?: string
}

interface ISingleChoiceBaseBlock extends IBlock {
  type: BLOCK_TYPE.SINGLE_CHOICE
  settings: BlockBaseSettings &
    SharedSettings & {
      /** Which UI type to render as */
      uiType: SINGLE_CHOICE_UI_TYPE
      /** Which rendering style should be used for image-enabled choices */
      imageType?: never | IMAGE_CHOICE_UI_SUBTYPE
      /** Which button layout option to use */
      buttonType?: never | BUTTON_CHOICE_LAYOUT_TYPE
      /**
       * Which choices to offer, if set inline (i.e. not through an uploaded file).
       *
       * If {@link DropdownSingleChoiceBlock.settings.choicesFileId} is set, then this
       * is expected to be empty and will be ignored.
       */
      choices: (IconChoice | ImageChoice)[]
      /**
       * From which file the options should be loaded from, if any.
       *
       * When set, it takes precedence over {@link DropdownSingleChoiceBlock.settings.choicesFileId} which is
       * expected to be empty.
       */
      choicesFileId?: never | FileId
    }
}

export interface ButtonSingleChoiceBlock extends ISingleChoiceBaseBlock {
  settings: BlockBaseSettings &
    SharedSettings & {
      uiType: SINGLE_CHOICE_UI_TYPE.BUTTON
      imageType?: never
      buttonType?: BUTTON_CHOICE_LAYOUT_TYPE
      choices: IconChoice[]
      choicesFileId?: never
    }
}

export interface RadioSingleChoiceBlock extends ISingleChoiceBaseBlock {
  settings: BlockBaseSettings &
    SharedSettings & {
      uiType: SINGLE_CHOICE_UI_TYPE.RADIO
      imageType?: never
      buttonType?: never
      choices: IconChoice[]
      choicesFileId?: never
    }
}

export interface DropdownSingleChoiceBlock extends ISingleChoiceBaseBlock {
  settings: BlockBaseSettings &
    SharedSettings & {
      uiType: SINGLE_CHOICE_UI_TYPE.DROPDOWN
      imageType?: never
      buttonType?: never
      choices: IconChoice[]
      choicesFileId?: never | FileId
    }
}

export interface ImageSingleChoiceBlock extends ISingleChoiceBaseBlock {
  settings: BlockBaseSettings &
    SharedSettings & {
      uiType: SINGLE_CHOICE_UI_TYPE.IMAGE
      imageType: IMAGE_CHOICE_UI_SUBTYPE
      buttonType?: never
      choices: (IconChoice | ImageChoice)[]
      choicesFileId?: never
    }
}

export type SingleChoiceBlock =
  | ButtonSingleChoiceBlock
  | RadioSingleChoiceBlock
  | DropdownSingleChoiceBlock
  | ImageSingleChoiceBlock

/**
 * Represents the value of a single choice block.
 */
export type SingleChoiceValue = string | null | undefined
